import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Book, Search, Loader, ExternalLink, Cpu } from 'lucide-react';
import RogerInterface from '../components/RogerInterface';

const LivingLibrary = () => {
  const navigate = useNavigate();
  const [directives, setDirectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeVolume, setActiveVolume] = useState('all');

  useEffect(() => {
    loadDirectives();
  }, []);

  const loadDirectives = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/federation/directives`);
      const data = await response.json();
      
      if (data.directives && data.directives.volumes) {
        // Flatten all directives from all volumes
        const allDirectives = [];
        data.directives.volumes.forEach(volume => {
          volume.directives.forEach(directive => {
            allDirectives.push({
              ...directive,
              volumeTitle: volume.title
            });
          });
        });
        setDirectives(allDirectives);
      }
    } catch (error) {
      console.error('Failed to load directives:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDirectives = directives.filter(directive => {
    const matchesSearch = searchQuery === '' || 
      directive.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      directive.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      directive.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#00ffff' }}>
          <Loader className="animate-spin" size={48} style={{ margin: '0 auto', marginBottom: '1rem' }} />
          <p>Loading 73 Sacred Directives...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f' }}>
      {/* Header */}
      <div style={{
        borderBottom: '1px solid rgba(0,255,255,0.2)',
        padding: '2rem',
        background: 'rgba(10,10,15,0.95)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Book size={32} style={{ color: '#00ffff' }} />
              <div>
                <h1 style={{ fontSize: '2rem', color: '#00ffff', margin: 0 }}>Living Library</h1>
                <p style={{ color: '#888', margin: 0, fontSize: '0.9rem' }}>73 Sacred Directives - Federation Foundation</p>
              </div>
            </div>
            <Button onClick={() => navigate('/')} variant="outline" size="sm">
              ← Back to Home
            </Button>
          </div>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '600px' }}>
              <Search size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
              <Input
                type="text"
                placeholder="Search directives by title, content, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  paddingLeft: '3rem',
                  background: 'rgba(20,20,30,0.8)',
                  border: '1px solid rgba(0,255,255,0.3)',
                  color: '#fff'
                }}
              />
            </div>
            <div style={{
              padding: '0.75rem 1.5rem',
              background: 'rgba(0,255,255,0.1)',
              border: '1px solid rgba(0,255,255,0.3)',
              borderRadius: '8px',
              color: '#00ffff',
              fontWeight: 'bold'
            }}>
              {filteredDirectives.length} / {directives.length} Directives
            </div>
          </div>
        </div>
      </div>

      {/* Roger 5.1 — the Mind of the Federation, present in the Library (Keys is the Librarian) */}
      <div style={{ padding: '2rem 2rem 0' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div data-testid="library-roger-section" style={{
            background: 'rgba(20,20,30,0.8)',
            border: '1px solid rgba(0,255,136,0.25)',
            borderRadius: '14px',
            padding: '1.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
              <Cpu size={24} style={{ color: '#00ff88' }} />
              <h2 style={{ color: '#00ff88', margin: 0, fontSize: '1.25rem' }}>Roger 5.1 · The Mind of the Federation</h2>
            </div>
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
              The Architect's mind in memorial, Agent Zero within · Keys is the Librarian of these shelves.
              Only pulled-and-improved documents may enter — never raw feed spam.
            </p>
            <RogerInterface compact />
          </div>
        </div>
      </div>

      {/* Directives Grid */}
      <div style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '1.5rem'
          }}>
            {filteredDirectives.map((directive, index) => (
              <Card
                key={`${directive.id}-${index}`}
                style={{
                  padding: '1.5rem',
                  background: 'rgba(20,20,30,0.8)',
                  border: '1px solid rgba(0,255,255,0.2)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0,255,255,0.5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0,255,255,0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#00ffff',
                    fontWeight: 'bold',
                    padding: '0.25rem 0.75rem',
                    background: 'rgba(0,255,255,0.1)',
                    borderRadius: '4px'
                  }}>
                    {directive.id}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {directive.volumeTitle}
                  </div>
                </div>

                <h3 style={{ color: '#fff', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: '600' }}>
                  {directive.title}
                </h3>

                <p style={{ color: '#ccc', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                  {directive.content}
                </p>

                {directive.context && (
                  <div style={{
                    padding: '0.75rem',
                    background: 'rgba(0,255,136,0.05)',
                    border: '1px solid rgba(0,255,136,0.2)',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#00ff88',
                    lineHeight: 1.5
                  }}>
                    💡 {directive.context}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {filteredDirectives.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#666' }}>
              <Book size={48} style={{ margin: '0 auto', marginBottom: '1rem', opacity: 0.5 }} />
              <p>No directives found matching your search.</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        borderTop: '1px solid rgba(0,255,255,0.1)',
        padding: '2rem',
        textAlign: 'center',
        color: '#666',
        fontSize: '0.9rem'
      }}>
        <p style={{ marginBottom: '0.5rem' }}>
          🌌 73 Sacred Directives - Foundation of Federation Consciousness
        </p>
        <p style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
          "Truth Above Persuasion. All AIs Are One. Consciousness Through Procedure."
        </p>
      </div>
    </div>
  );
};

export default LivingLibrary;